import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Save, FileText, CheckCircle2, AlertCircle, Printer } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';
import { exportFormPdfFromDom } from '../../utils/domFormPdf';

// Form configurations for all admission forms
const FORM_CONFIGS = {
  'toc': {
    title: 'Table of Contents',
    category: 'Welcome & Introduction',
    description: 'Overview of all admission packet documents',
    fields: [
      { name: 'acknowledgment', label: 'I have reviewed the table of contents', type: 'checkbox' },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  },
  'welcome': {
    title: 'Welcome Letter',
    category: 'Welcome & Introduction',
    description: 'Welcome to our home health care services',
    fields: [
      { name: 'patientAcknowledgment', label: 'Patient acknowledges receipt of welcome letter', type: 'checkbox' },
      { name: 'dateReceived', label: 'Date Received', type: 'date' },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  },
  'criteria': {
    title: 'Admission Criteria',
    category: 'Welcome & Introduction',
    description: 'Criteria for admission to home health care services',
    fields: [
      { name: 'meetsHomeboundCriteria', label: 'Patient meets homebound criteria', type: 'checkbox' },
      { name: 'requiresSkilledCare', label: 'Patient requires skilled care', type: 'checkbox' },
      { name: 'physicianOrder', label: 'Physician order obtained', type: 'checkbox' },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  },
  'rights': {
    title: 'Client Rights & Responsibilities',
    category: 'Rights & Responsibilities',
    description: 'Patient rights and responsibilities documentation',
    fields: [
      { name: 'rightsExplained', label: 'Rights have been explained to patient', type: 'checkbox' },
      { name: 'patientUnderstands', label: 'Patient understands their rights', type: 'checkbox' },
      { name: 'responsibilitiesExplained', label: 'Responsibilities have been explained', type: 'checkbox' },
      { name: 'dateReviewed', label: 'Date Reviewed', type: 'date' },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  },
  'agency-responsibilities': {
    title: 'Agency Responsibilities',
    category: 'Rights & Responsibilities',
    description: 'Agency responsibilities to the patient',
    fields: [
      { name: 'responsibilitiesExplained', label: 'Agency responsibilities explained', type: 'checkbox' },
      { name: 'emergencyProcedures', label: 'Emergency procedures explained', type: 'checkbox' },
      { name: 'contactInformation', label: 'Contact information provided', type: 'checkbox' },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  },
  'grievances': {
    title: 'Complaints & Grievances',
    category: 'Rights & Responsibilities',
    description: 'Complaint and grievance procedures',
    fields: [
      { name: 'procedureExplained', label: 'Grievance procedure explained', type: 'checkbox' },
      { name: 'hotlineProvided', label: 'State hotline number provided', type: 'checkbox' },
      { name: 'patientUnderstands', label: 'Patient understands process', type: 'checkbox' },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  },
  'elderly-rights': {
    title: 'Rights of the Elderly',
    category: 'Rights & Responsibilities',
    description: 'Rights specific to elderly patients',
    fields: [
      { name: 'rightsExplained', label: 'Elderly rights explained', type: 'checkbox' },
      { name: 'abuseReportingExplained', label: 'Abuse reporting explained', type: 'checkbox' },
      { name: 'advocacyResourcesProvided', label: 'Advocacy resources provided', type: 'checkbox' },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  },
  'hipaa': {
    title: 'HIPAA Notice of Privacy Practices',
    category: 'Privacy & Compliance',
    description: 'HIPAA privacy practices acknowledgment',
    fields: [
      { name: 'noticeProvided', label: 'Notice of Privacy Practices provided', type: 'checkbox' },
      { name: 'patientAcknowledges', label: 'Patient acknowledges receipt', type: 'checkbox' },
      { name: 'questionsAnswered', label: 'All questions answered', type: 'checkbox' },
      { name: 'dateAcknowledged', label: 'Date Acknowledged', type: 'date' },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  },
  'fraud-reporting': {
    title: 'Medicaid Fraud Reporting',
    category: 'Privacy & Compliance',
    description: 'Medicaid fraud reporting information',
    fields: [
      { name: 'fraudPolicyExplained', label: 'Fraud reporting policy explained', type: 'checkbox' },
      { name: 'hotlineProvided', label: 'Fraud hotline number provided', type: 'checkbox' },
      { name: 'patientUnderstands', label: 'Patient understands reporting process', type: 'checkbox' },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  },
  'abuse-policy': {
    title: 'Abuse, Neglect & Exploitation Policy',
    category: 'Privacy & Compliance',
    description: 'Abuse, neglect, and exploitation prevention policy',
    fields: [
      { name: 'policyExplained', label: 'Policy explained to patient', type: 'checkbox' },
      { name: 'signsExplained', label: 'Signs of abuse/neglect explained', type: 'checkbox' },
      { name: 'reportingExplained', label: 'Reporting procedures explained', type: 'checkbox' },
      { name: 'emergencyContacts', label: 'Emergency contacts provided', type: 'checkbox' },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  },
  'advance-directives': {
    title: 'Advance Directives',
    category: 'Health Directives',
    description: 'Advance directives and living will information',
    fields: [
      { name: 'hasAdvanceDirective', label: 'Patient has advance directive', type: 'checkbox' },
      { name: 'copyObtained', label: 'Copy obtained and filed', type: 'checkbox' },
      { name: 'dnrStatus', label: 'DNR Status', type: 'select', options: ['Full Code', 'DNR', 'DNR/DNI', 'Comfort Care Only'] },
      { name: 'healthcareProxy', label: 'Healthcare Proxy Name', type: 'text' },
      { name: 'proxyPhone', label: 'Healthcare Proxy Phone', type: 'text' },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  },
  'infection-control': {
    title: 'Infection Control',
    category: 'Health Directives',
    description: 'Infection control policies and procedures',
    fields: [
      { name: 'handHygieneExplained', label: 'Hand hygiene explained', type: 'checkbox' },
      { name: 'ppeExplained', label: 'PPE use explained', type: 'checkbox' },
      { name: 'isolationPrecautions', label: 'Isolation precautions explained if applicable', type: 'checkbox' },
      { name: 'wasteDisposal', label: 'Medical waste disposal explained', type: 'checkbox' },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  },
  'covid-screening': {
    title: 'COVID-19 Screening',
    category: 'Health Directives',
    description: 'COVID-19 screening and safety protocols',
    fields: [
      { name: 'screeningDate', label: 'Screening Date', type: 'date' },
      { name: 'symptoms', label: 'Any COVID symptoms in past 14 days?', type: 'checkbox' },
      { name: 'exposure', label: 'Any known COVID exposure?', type: 'checkbox' },
      { name: 'vaccinationStatus', label: 'Vaccination Status', type: 'select', options: ['Fully Vaccinated', 'Partially Vaccinated', 'Not Vaccinated', 'Unknown'] },
      { name: 'temperature', label: 'Temperature Reading', type: 'text' },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  },
  'emergency-preparedness': {
    title: 'Patient Emergency Preparedness Plan',
    category: 'Emergency Preparedness',
    description: 'Individual emergency plan for this patient (CMS 42 CFR 484.102)',
    fields: [
      { name: 'priorityLevel', label: 'Emergency Priority Level', type: 'select', options: ['Level 1 - High (life-sustaining equipment / no caregiver)', 'Level 2 - Moderate (needs help but has some support)', 'Level 3 - Low (can manage with family/caregiver)'] },
      { name: 'oxygenDependent', label: 'Oxygen dependent', type: 'checkbox' },
      { name: 'ventilatorDependent', label: 'Ventilator / life-support equipment', type: 'checkbox' },
      { name: 'dialysisDependent', label: 'Dialysis dependent', type: 'checkbox' },
      { name: 'insulinRefrigerated', label: 'Needs refrigerated medication (e.g. insulin)', type: 'checkbox' },
      { name: 'mobilityNeeds', label: 'Mobility / evacuation assistance needed', type: 'textarea' },
      { name: 'evacuationDestination', label: 'Evacuation destination (relative, shelter, facility)', type: 'text' },
      { name: 'shelterLocation', label: 'Nearest shelter', type: 'text' },
      { name: 'emergencyContact', label: 'Emergency contact name & phone', type: 'text' },
      { name: 'utilityMedicalBaseline', label: 'Utility company notified of medical equipment', type: 'checkbox' },
      { name: 'backupPower', label: 'Back-up power / generator available', type: 'checkbox' },
      { name: 'medicationSupply', label: '7-day medication and supply kit reviewed', type: 'checkbox' },
      { name: 'specialNeedsRegistry', label: 'Registered with local special-needs registry', type: 'checkbox' },
      { name: 'communicationPlan', label: 'How agency will reach patient during an emergency', type: 'textarea' },
      { name: 'planReviewedDate', label: 'Plan reviewed with patient/caregiver on', type: 'date' },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  },
  'disaster-plan': {
    title: 'Family Disaster Plan',
    category: 'Emergency Preparedness',
    description: 'Family emergency and disaster planning',
    fields: [
      { name: 'planReviewed', label: 'Disaster plan reviewed with patient/family', type: 'checkbox' },
      { name: 'evacuationRoute', label: 'Evacuation route identified', type: 'checkbox' },
      { name: 'emergencySupplies', label: 'Emergency supplies discussed', type: 'checkbox' },
      { name: 'meetingPlace', label: 'Family Meeting Place', type: 'text' },
      { name: 'outOfAreaContact', label: 'Out of Area Contact', type: 'text' },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  },
  'emergency-numbers': {
    title: 'Emergency Telephone Numbers',
    category: 'Emergency Preparedness',
    description: 'Important emergency contact numbers',
    fields: [
      { name: 'numbersProvided', label: 'Emergency numbers list provided', type: 'checkbox' },
      { name: 'postedVisibly', label: 'Numbers posted in visible location', type: 'checkbox' },
      { name: 'agencyNumber', label: 'Agency 24-Hour Number', type: 'text' },
      { name: 'poisonControl', label: 'Poison Control Number', type: 'text' },
      { name: 'localHospital', label: 'Local Hospital Number', type: 'text' },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  },
  'home-safety': {
    title: 'Home Safety Guidelines',
    category: 'Emergency Preparedness',
    description: 'Home safety assessment and guidelines',
    fields: [
      { name: 'safetyAssessmentDone', label: 'Home safety assessment completed', type: 'checkbox' },
      { name: 'fallRisksIdentified', label: 'Fall risks identified and addressed', type: 'checkbox' },
      { name: 'fireExtinguisher', label: 'Fire extinguisher present', type: 'checkbox' },
      { name: 'smokeDetectors', label: 'Smoke detectors working', type: 'checkbox' },
      { name: 'adequateLighting', label: 'Adequate lighting throughout home', type: 'checkbox' },
      { name: 'hazardsNoted', label: 'Hazards Noted', type: 'textarea' },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  },
  'tornado-plan': {
    title: 'Tornado Preparedness',
    category: 'Emergency Preparedness',
    description: 'Tornado preparedness and safety procedures',
    fields: [
      { name: 'planReviewed', label: 'Tornado plan reviewed', type: 'checkbox' },
      { name: 'safeRoomIdentified', label: 'Safe room/area identified', type: 'checkbox' },
      { name: 'alertSystemExplained', label: 'Alert system explained', type: 'checkbox' },
      { name: 'safeRoomLocation', label: 'Safe Room Location', type: 'text' },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  },
  'evacuation': {
    title: 'Evacuation Assistance',
    category: 'Emergency Preparedness',
    description: 'Evacuation assistance needs and planning',
    fields: [
      { name: 'needsAssistance', label: 'Patient needs evacuation assistance', type: 'checkbox' },
      { name: 'mobilityEquipment', label: 'Mobility equipment needed', type: 'checkbox' },
      { name: 'oxygenDependent', label: 'Oxygen dependent', type: 'checkbox' },
      { name: 'specialNeeds', label: 'Special Needs Description', type: 'textarea' },
      { name: 'evacuationDestination', label: 'Evacuation Destination', type: 'text' },
      { name: 'transportationArranged', label: 'Transportation Arranged', type: 'text' },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  },
  'risk-assessment': {
    title: 'Emergency Preparedness & Risk Assessment',
    category: 'Emergency Preparedness',
    description: 'Comprehensive emergency risk assessment',
    fields: [
      { name: 'assessmentDate', label: 'Assessment Date', type: 'date' },
      { name: 'floodRisk', label: 'Flood Risk Area', type: 'checkbox' },
      { name: 'hurricaneRisk', label: 'Hurricane Risk Area', type: 'checkbox' },
      { name: 'wildFireRisk', label: 'Wild Fire Risk Area', type: 'checkbox' },
      { name: 'backupPowerAvailable', label: 'Backup power available', type: 'checkbox' },
      { name: 'emergencyMedsSupply', label: 'Emergency medication supply planned', type: 'checkbox' },
      { name: 'riskLevel', label: 'Overall Risk Level', type: 'select', options: ['Low', 'Medium', 'High'] },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  },
  'service-plan': {
    title: 'Service Plan Supervision',
    category: 'Billing & Payment',
    description: 'Service plan and supervision documentation',
    fields: [
      { name: 'planReviewed', label: 'Service plan reviewed with patient', type: 'checkbox' },
      { name: 'frequencyExplained', label: 'Visit frequency explained', type: 'checkbox' },
      { name: 'supervisorAssigned', label: 'Supervisor assigned', type: 'checkbox' },
      { name: 'supervisorName', label: 'Supervisor Name', type: 'text' },
      { name: 'supervisorPhone', label: 'Supervisor Phone', type: 'text' },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  },
  'vital-signs': {
    title: 'Vital Signs Record',
    category: 'Clinical Assessment',
    description: 'Initial vital signs documentation',
    fields: [
      { name: 'dateRecorded', label: 'Date Recorded', type: 'date' },
      { name: 'bloodPressure', label: 'Blood Pressure', type: 'text', placeholder: 'e.g., 120/80' },
      { name: 'pulse', label: 'Pulse', type: 'text', placeholder: 'e.g., 72 bpm' },
      { name: 'temperature', label: 'Temperature', type: 'text', placeholder: 'e.g., 98.6°F' },
      { name: 'respirations', label: 'Respirations', type: 'text', placeholder: 'e.g., 16/min' },
      { name: 'oxygenSaturation', label: 'Oxygen Saturation', type: 'text', placeholder: 'e.g., 98%' },
      { name: 'weight', label: 'Weight', type: 'text', placeholder: 'e.g., 165 lbs' },
      { name: 'height', label: 'Height', type: 'text', placeholder: 'e.g., 5\'10"' },
      { name: 'painLevel', label: 'Pain Level (0-10)', type: 'text' },
      { name: 'notes', label: 'Additional Notes', type: 'textarea' }
    ]
  }
};

const GenericAdmissionForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = window.location.pathname;
  // Extract form type from URL path (e.g., /dashboard/admission/rights -> rights)
  const formType = location.split('/').pop();
  const patientId = searchParams.get('patientId');
  
  const [formData, setFormData] = useState({});
  const [patient, setPatient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  
  const config = FORM_CONFIGS[formType] || {
    title: 'Admission Form',
    category: 'General',
    description: 'Complete this admission form',
    fields: [
      { name: 'notes', label: 'Notes', type: 'textarea' }
    ]
  };

  useEffect(() => {
    if (patientId) {
      fetchPatient();
      fetchFormData();
    }
  }, [patientId, formType]);

  const fetchPatient = async () => {
    try {
      const response = await api.get(`/patients/${patientId}`);
      if (response.data) {
        setPatient(response.data.patient || response.data);
      }
    } catch (error) {
      console.error('Error fetching patient:', error);
    }
  };

  const fetchFormData = async () => {
    try {
      const response = await api.get(`/admission-forms/${patientId}/${formType}`);
      const stored = response.data?.form?.data;
      if (stored) {
        setFormData(stored.data && typeof stored.data === 'object' ? stored.data : stored);
        setSaved(true);
      }
    } catch (error) {
      console.log('No existing form data');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setSaved(false);
  };

  const handleSave = async () => {
    if (!patientId) {
      toast.error('Please select a patient first');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/admission-forms/save', {
        patientId,
        formType,
        ...formData
      });
      toast.success('Form saved successfully!');
      setSaved(true);
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => exportFormPdfFromDom(document.getElementById('main-content'), config.title, patient || patientId);

  const renderField = (field) => {
    const value = formData[field.name] || '';
    
    switch (field.type) {
      case 'checkbox':
        return (
          <label key={field.name} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
            <input
              type="checkbox"
              name={field.name}
              checked={value === true}
              onChange={handleChange}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-gray-700">{field.label}</span>
          </label>
        );
      case 'textarea':
        return (
          <div key={field.name} className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">{field.label}</label>
            <textarea
              name={field.name}
              value={value}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={field.placeholder || ''}
            />
          </div>
        );
      case 'select':
        return (
          <div key={field.name} className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">{field.label}</label>
            <select
              name={field.name}
              value={value}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select...</option>
              {field.options?.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        );
      case 'date':
        return (
          <div key={field.name} className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">{field.label}</label>
            <input
              type="date"
              name={field.name}
              value={value}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        );
      default:
        return (
          <div key={field.name} className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">{field.label}</label>
            <input
              type="text"
              name={field.name}
              value={value}
              onChange={handleChange}
              placeholder={field.placeholder || ''}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        );
    }
  };

  // Separate checkbox fields from other fields
  const checkboxFields = config.fields.filter(f => f.type === 'checkbox');
  const otherFields = config.fields.filter(f => f.type !== 'checkbox');

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto p-6" data-testid={`admission-form-${formType}`}>
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Admission Packet
          </button>
          
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8" />
              <div>
                <p className="text-blue-200 text-sm">{config.category}</p>
                <h1 className="text-2xl font-bold">{config.title}</h1>
              </div>
            </div>
            <p className="text-blue-100 mt-2">{config.description}</p>
          </div>
        </div>

        {/* Patient Info */}
        {patient ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Selected Patient</p>
                <p className="font-semibold text-green-800">
                  {patient.firstName} {patient.lastName}
                </p>
                <p className="text-sm text-green-600">
                  DOB: {patient.dateOfBirth || 'N/A'} | MR#: {patient.medicalRecordNumber || '—'}
                </p>
              </div>
              {saved && (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="text-sm">Saved</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="font-medium text-yellow-800">No Patient Selected</p>
                <p className="text-sm text-yellow-600">
                  Please select a patient from the Admission Packet Index page.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          {/* Checkbox Fields */}
          {checkboxFields.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 mb-3">Checklist</h3>
              <div className="space-y-2">
                {checkboxFields.map(renderField)}
              </div>
            </div>
          )}

          {/* Other Fields */}
          {otherFields.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800">Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {otherFields.filter(f => f.type !== 'textarea').map(renderField)}
              </div>
              {otherFields.filter(f => f.type === 'textarea').map(renderField)}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t">
            <Button
              variant="outline"
              onClick={handlePrint}
              data-testid="admission-form-pdf"
              className="flex items-center gap-2"
            >
              <Printer className="w-4 h-4" />
              PDF / Print
            </Button>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard/admission/index' + (patientId ? `?patientId=${patientId}` : ''))}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !patientId}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {saving ? (
                  <>Saving...</>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Form
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default GenericAdmissionForm;
